import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useConfig } from "@/contexts/ConfigContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";

export const GROUP_LABELS = { hpp: "HPP / Biaya Proyek", operational: "Beban Operasional", other: "Beban Lain-lain" };

const today = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const emptyLine = () => ({ key: Math.random().toString(36).slice(2, 8), account_code: "", description: "", amount: "" });
const digits = (v) => String(v ?? "").replace(/[^\d]/g, "");

export const ExpenseFormDialog = ({ open, onOpenChange, dealId, expense, defaultAccountCode, onSaved }) => {
  const { t } = useLang();
  const cfg = useConfig();
  const editing = !!expense;

  const [date, setDate] = useState(today());
  const [beneficiary, setBeneficiary] = useState("");
  const [beneficiaryId, setBeneficiaryId] = useState(null);
  const [showList, setShowList] = useState(false);
  const [reference, setReference] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setReason("");
    if (expense) {
      setDate(expense.date || today());
      setBeneficiary(expense.beneficiary_name || "");
      setBeneficiaryId(expense.beneficiary_id || null);
      setReference(expense.reference || "");
      setLines((expense.lines || []).map((l) => ({
        key: Math.random().toString(36).slice(2, 8),
        account_code: l.account_code || "",
        description: l.description || "",
        amount: l.amount ? String(l.amount) : "",
      })));
    } else {
      setDate(today());
      setBeneficiary("");
      setBeneficiaryId(null);
      setReference("");
      setLines([{ ...emptyLine(), account_code: defaultAccountCode || "" }]);
    }
  }, [open, expense, defaultAccountCode]);

  const accounts = cfg?.activeAccounts || [];
  const grouped = useMemo(() => {
    const out = {};
    accounts.forEach((a) => { (out[a.group] = out[a.group] || []).push(a); });
    return out;
  }, [accounts]);

  const matches = useMemo(() => {
    const q = beneficiary.trim().toLowerCase();
    const list = cfg?.activeBeneficiaries || [];
    if (!q) return list.slice(0, 6);
    return list.filter((b) => b.name.toLowerCase().includes(q)).slice(0, 6);
  }, [beneficiary, cfg?.activeBeneficiaries]);

  const isNewBeneficiary =
    beneficiary.trim().length > 0 &&
    !(cfg?.beneficiaries || []).some((b) => b.name.toLowerCase() === beneficiary.trim().toLowerCase());

  const total = lines.reduce((s, l) => s + Number(digits(l.amount) || 0), 0);

  const clearError = (key) => setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  const setLine = (idx, patch) => {
    if (patch.account_code !== undefined) clearError(`account_${idx}`);
    if (patch.amount !== undefined) clearError(`amount_${idx}`);
    setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const validate = () => {
    const next = {};
    if (!date) next.date = t("field_required");
    if (!beneficiary.trim()) next.beneficiary = t("field_required");
    if (editing && !reason.trim()) next.reason = t("field_required");
    lines.forEach((l, i) => {
      if (!l.account_code) next[`account_${i}`] = t("field_required");
      if (!Number(digits(l.amount))) next[`amount_${i}`] = t("amount_must_be_positive");
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) { toast.error(t("check_the_form")); return; }
    setSaving(true);
    try {
      const payload = {
        date,
        beneficiary_id: beneficiaryId,
        beneficiary_name: beneficiary.trim(),
        reference: reference.trim() || null,
        lines: lines.map((l) => ({
          account_code: l.account_code,
          description: l.description?.trim() || null,
          amount: Number(digits(l.amount) || 0),
        })),
        reason: editing ? reason.trim() : null,
      };
      if (editing) await api.put(`/expenses/${expense.id}`, payload);
      else await api.post(`/deals/${dealId}/expenses`, payload);
      toast.success(editing ? t("expense_updated") : t("expense_posted"));
      await cfg.reload();
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "No changes to save") toast.info(t("no_changes"));
      else toast.error(typeof detail === "string" ? detail : "Error");
    } finally {
      setSaving(false);
    }
  };

  const errText = (key) => errors[key] && <p className="mt-1 text-[11px] text-rose-600">{errors[key]}</p>;  const inputCls = (key) => `h-9 ${errors[key] ? "border-rose-400 focus-visible:ring-rose-300" : ""}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto crm-scroll" data-testid="expense-form">
        <DialogHeader>
          <DialogTitle>{editing ? t("edit_expense") : t("record_expense")}</DialogTitle>
          <DialogDescription className="sr-only">{t("expenses")}</DialogDescription>
        </DialogHeader>

        {editing && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="expense-edit-banner">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{t("expense_edit_warning").replace("{number}", expense.number)}</span>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">{t("transaction_date")}*</Label>
            <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); clearError("date"); }}
              data-testid="expense-date-input" className={inputCls("date")} />
            {errText("date")}
          </div>
          <div className="relative">
            <Label className="text-xs">{t("beneficiary")}*</Label>
            <Input
              value={beneficiary}
              data-testid="expense-beneficiary-input"
              placeholder={t("beneficiary_placeholder")}
              className={inputCls("beneficiary")}
              onChange={(e) => { setBeneficiary(e.target.value); setBeneficiaryId(null); setShowList(true); clearError("beneficiary"); }}
              onFocus={() => setShowList(true)}
              onBlur={() => setTimeout(() => setShowList(false), 150)}
            />
            {showList && matches.length > 0 && (
              <div className="absolute z-20 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="beneficiary-options">
                {matches.map((b) => (
                  <button key={b.id} type="button" data-testid={`beneficiary-option-${b.id}`}
                    className="block w-full px-3 py-2 text-left text-xs hover:bg-orange-50"
                    onMouseDown={() => { setBeneficiary(b.name); setBeneficiaryId(b.id); setShowList(false); }}>
                    {b.name} <span className="text-slate-400">· {t(`ben_${b.type}`)}</span>
                  </button>
                ))}
              </div>
            )}
            {errText("beneficiary")}
            {isNewBeneficiary && !errors.beneficiary && (
              <p className="mt-1 flex items-center gap-1 text-[11px] text-orange-700" data-testid="new-beneficiary-hint">
                <Info className="h-3 w-3" /> {t("will_be_added_to_master")}
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">{t("number")}</Label>
            <Input readOnly value={expense?.number || t("auto_number")} data-testid="expense-number-input"
              className="h-9 mono bg-slate-50 text-slate-500" />
          </div>
          <div>
            <Label className="text-xs">{t("reference")}</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)}
              placeholder={t("reference_placeholder")} data-testid="expense-reference-input" className="h-9" />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between bg-slate-50 px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{t("expense_lines")}</span>
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid="expense-add-line-btn"
              onClick={() => setLines([...lines, emptyLine()])}>
              <Plus className="h-3 w-3" /> {t("add_line")}
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-3 py-2 w-[42%]">{t("expense_account")}*</th>
                  <th className="px-3 py-2">{t("description")}</th>
                  <th className="px-3 py-2 w-40 text-right">{t("amount")} (Rp)*</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.key} className="border-b border-slate-100 align-top" data-testid={`expense-line-${i}`}>
                    <td className="px-3 py-2">
                      <select
                        value={l.account_code}
                        data-testid={`expense-line-account-${i}`}
                        onChange={(e) => setLine(i, { account_code: e.target.value })}
                        className={`h-9 w-full rounded-md border bg-white px-2 text-xs ${errors[`account_${i}`] ? "border-rose-400" : "border-slate-200"}`}
                      >
                        <option value="">— {t("select_account")} —</option>
                        {Object.entries(grouped).map(([group, items]) => (
                          <optgroup key={group} label={GROUP_LABELS[group] || group}>
                            {items.map((a) => <option key={a.code} value={a.code}>{`${a.code} · ${a.name}`}</option>)}
                          </optgroup>
                        ))}
                      </select>
                      {errText(`account_${i}`)}
                    </td>
                    <td className="px-3 py-2">
                      <Input className="h-9 text-xs" value={l.description} data-testid={`expense-line-desc-${i}`}
                        onChange={(e) => setLine(i, { description: e.target.value })} />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        className={`h-9 text-xs mono text-right ${errors[`amount_${i}`] ? "border-rose-400" : ""}`}
                        inputMode="numeric"
                        data-testid={`expense-line-amount-${i}`}
                        value={l.amount ? Number(digits(l.amount)).toLocaleString("id-ID") : ""}
                        onChange={(e) => setLine(i, { amount: digits(e.target.value) })}
                      />
                      {errText(`amount_${i}`)}
                    </td>
                    <td className="px-2 py-2">
                      {lines.length > 1 && (
                        <button data-testid={`expense-remove-line-${i}`} onClick={() => setLines(lines.filter((_, x) => x !== i))}
                          className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 text-slate-600">
                  <td className="px-3 py-2 font-semibold" colSpan={2}>{t("sub_total")}</td>
                  <td className="px-3 py-2 text-right mono font-semibold" data-testid="expense-subtotal">{formatIDR(total)}</td>
                  <td />
                </tr>
                <tr className="bg-slate-50 text-slate-900">
                  <td className="px-3 py-2 font-bold" colSpan={2}>{t("total")}</td>
                  <td className="px-3 py-2 text-right mono font-bold" data-testid="expense-total">{formatIDR(total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {editing && (
          <div>
            <Label className="text-xs">{t("change_reason")}*</Label>
            <Input value={reason} onChange={(e) => { setReason(e.target.value); clearError("reason"); }} data-testid="expense-reason-input"
              className={inputCls("reason")} placeholder={t("change_reason_placeholder")} />
            {errText("reason")}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="expense-save-btn" className="bg-orange-500 hover:bg-orange-600">
            {editing ? t("save_changes") : t("save_and_post")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseFormDialog;
